// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetReportToolbar } from '@/report/components/asset-report-toolbar';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import {
  DataGridPremium,
  DataGridPremiumProps,
  GridAggregationPosition,
  GridGroupNode,
  GridToolbarProps,
  gridClasses,
} from '@mui/x-data-grid-premium';
import { useCallback, useMemo } from 'react';

const DEFAULT_EMPTY_STATE = 'No data to display';

/**
 * Returns the position of the aggregation row.
 *
 * @param node - The group node for which to determine the aggregation position.
 */
function getAggregationPosition(node: GridGroupNode): GridAggregationPosition {
  if (node.groupingField === null) {
    return 'footer';
  }

  return 'inline';
}

interface Props extends DataGridPremiumProps {
  /**
   * The maximum height of the table. This is used to make the table scrollable.
   */
  maximumHeight: number;

  /**
   * The empty state message to display when there is no data.
   *
   * @default 'No data to display'
   */
  emptyState?: string;

  /**
   * A list of column names in the order they should appear in the exported
   * file. Any columns not included in this list will be exported in the order
   * they appear in the data.
   */
  exportColumnOrder: string[];

  /**
   * A callback fired when the user clicks the export button that returns the
   * name of the file to be exported. This file name should not include a file
   * extension.
   */
  getFileName: () => string;
}

/**
 * A data table that displays rows of data in a report.
 */
export function ReportTable(props: Props) {
  const theme = useTheme();

  const emptyState = props.emptyState ?? DEFAULT_EMPTY_STATE;
  const { apiRef, ...dataGridProps } = props;

  // A new identity here remounts the toolbar and drops its row-expansion state.
  const renderToolbar = useCallback(
    (toolbarProps: GridToolbarProps) => {
      return (
        <AssetReportToolbar
          {...toolbarProps}
          columnOrder={props.exportColumnOrder}
          getFileName={props.getFileName}
        />
      );
    },
    [props.exportColumnOrder, props.getFileName]
  );

  const renderEmptyOverlay = useCallback(() => {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography>{emptyState}</Typography>
      </Box>
    );
  }, [emptyState]);

  const gridSx = useMemo(
    () => ({
      ...props.sx,
      '--DataGrid-rowBorderColor': theme.palette.divider,
      [`& .${gridClasses.cell}`]: {
        display: 'flex',
        alignItems: 'center',
      },
      [`& .${gridClasses.footerCell}`]: {
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
      },
      [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
        outline: 'none',
      },
      [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
        {
          outline: 'none',
        },
    }),
    [props.sx, theme]
  );

  const slots = useMemo(
    () => ({ toolbar: renderToolbar, noRowsOverlay: renderEmptyOverlay }),
    [renderToolbar, renderEmptyOverlay]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        boxShadow: theme.shadows[6],
        maxHeight: props.maximumHeight,
        // Material UI reports an error if the initial container has a height
        // of zero.
        minHeight: 1,
      }}
    >
      <DataGridPremium
        hideFooter
        showToolbar
        disableColumnMenu
        disableColumnResize
        disableColumnReorder
        disableRowSelectionOnClick
        density="compact"
        slots={slots}
        getAggregationPosition={getAggregationPosition}
        {...dataGridProps}
        apiRef={apiRef}
        sx={gridSx}
      />
    </Paper>
  );
}
